/**
 * DOM lobby: nickname, quick play vs bot, create/join private rooms (room
 * codes), online players + challenges, live games to spectate, leaderboard,
 * and lobby chat. On joining any room it hands control to the GameScene.
 *
 * Presence: heartbeats every 4s while visible; the tab announces `away` the
 * moment it is hidden (keepalive beacon) so the server can keep the seat with
 * an accurate status, and announces `online` + repolls immediately on return.
 * Real departures (tab close / navigation) fire a `leave` beacon on pagehide.
 */
import { loadPrefs, getPrefs, updatePrefs, getUserId } from '../storage.js';
import { net, Rest } from '../net.js';
import { Sound, unlockAudio } from '../audio.js';
import { Music } from '../music.js';

let pollTimer: ReturnType<typeof setInterval> | null = null;
let listeners: AbortController | null = null;
let chatAfter = 0;
let entering = false;

function el(tag: string, cls?: string, html?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

function statusChip(status: string): string {
  if (status === 'away') return '<span class="chip away">away</span>';
  if (status === 'in-game') return '<span class="chip ingame">in game</span>';
  return '<span class="chip online"></span>';
}

export async function mountLobby(onEnter: () => void): Promise<void> {
  await loadPrefs();
  const prefs = getPrefs();
  entering = false;

  const root = el('div', 'lobby');
  root.id = 'lobby';

  root.innerHTML = `
    <div class="lobby-window glass">
      <div class="lobby-head">
        <div class="brand"><span class="logo">⛳</span><span>jgolf</span></div>
        <div class="name-row">
          <input id="nick" class="nick" maxlength="20" value="${escapeHtml(prefs.nickname)}" />
        </div>
      </div>
      <div class="lobby-body">
        <div class="lobby-main">
          <div class="play-row">
            <button class="btn primary big" id="quick">▶ Quick Play vs Bot</button>
          </div>
          <div class="opts">
            <label>Difficulty
              <select id="diff"><option value="easy">Easy</option><option value="medium" selected>Medium</option><option value="hard">Hard</option><option value="impossible">Impossible</option></select>
            </label>
            <label>Holes
              <select id="holes"><option>3</option><option selected>9</option><option>18</option></select>
            </label>
          </div>
          <div class="play-row two">
            <button class="btn" id="create">Create Private Room</button>
            <div class="join"><input id="code" class="code" placeholder="ABC" maxlength="3" /><button class="btn" id="join">Join</button></div>
          </div>
          <div class="section-title">Online players</div>
          <div id="players" class="players"></div>
          <div id="challenges" class="challenges"></div>
          <div class="section-title" id="games-title" style="display:none">Live games</div>
          <div id="games" class="players"></div>
        </div>
        <div class="lobby-side">
          <div class="section-title">Lobby chat</div>
          <div id="chat" class="chat"></div>
          <div class="chat-input"><input id="chatbox" placeholder="Say hi…" maxlength="200" /><button class="btn small" id="send">Send</button></div>
          <div class="section-title">Leaderboard</div>
          <div id="leaderboard" class="leaderboard"><span class="muted">No games recorded yet</span></div>
          <div class="settings">
            <label class="row"><span>Sound</span><input type="checkbox" id="sound" ${prefs.sound ? 'checked' : ''} /></label>
            <label class="row"><span>Music</span><input type="checkbox" id="music" ${prefs.music ? 'checked' : ''} /></label>
            <label class="row"><span>Music volume</span><input type="range" id="musicvol" min="0" max="100" value="${Math.round(prefs.musicVolume * 100)}" /></label>
          </div>
        </div>
      </div>
      <div class="lobby-status" id="status">Connecting…</div>
    </div>`;
  document.body.append(root);

  // The lobby is the first meaningful UI — retire the first-load splash now.
  const splash = document.getElementById('splash');
  if (splash) {
    splash.classList.add('hide');
    setTimeout(() => splash.remove(), 600);
  }

  const $ = <T extends HTMLElement>(sel: string) => root.querySelector(sel) as T;

  const nick = $<HTMLInputElement>('#nick');
  nick.onchange = () => updatePrefs({ nickname: nick.value.trim().slice(0, 20) || prefs.nickname });

  $('#sound').addEventListener('change', (e) => updatePrefs({ sound: (e.target as HTMLInputElement).checked }));
  $('#music').addEventListener('change', (e) => {
    void updatePrefs({ music: (e.target as HTMLInputElement).checked });
    if (getPrefs().music) Music.playLobby();
    else Music.refreshSettings();
  });
  $('#musicvol').addEventListener('input', (e) => {
    void updatePrefs({ musicVolume: parseInt((e.target as HTMLInputElement).value, 10) / 100 });
    Music.refreshSettings();
  });
  Music.playLobby();
  const codeInput = $<HTMLInputElement>('#code');
  codeInput.addEventListener('input', () => {
    codeInput.value = codeInput.value.replace(/[^a-z]/gi, '').toUpperCase().slice(0, 3);
  });

  const enter = async (joinFn: () => Promise<void>) => {
    if (entering) return;
    entering = true;
    unlockAudio();
    Sound.ui();
    try {
      await joinFn();
      cleanup();
      root.remove();
      onEnter();
    } catch (err) {
      entering = false;
      $('#status').textContent = `Error: ${(err as Error).message}`;
    }
  };

  $('#quick').onclick = () =>
    enter(async () => {
      const diff = $<HTMLSelectElement>('#diff').value;
      const holeCount = Number($<HTMLSelectElement>('#holes').value);
      const { roomId } = await Rest.createMatch({ vsBot: true, botDifficulty: diff, holeCount });
      await net.joinById(roomId);
    });

  $('#create').onclick = () =>
    enter(async () => {
      const holeCount = Number($<HTMLSelectElement>('#holes').value);
      const { roomId, code } = await Rest.createMatch({ holeCount, expectedPlayers: 2 });
      await net.joinById(roomId);
      setTimeout(() => alert(`Share this room code: ${code}`), 200);
    });

  $('#join').onclick = () =>
    enter(async () => {
      const code = $<HTMLInputElement>('#code').value.trim().toUpperCase();
      if (!code) throw new Error('Enter a code');
      const { roomId } = await Rest.matchByCode(code);
      await net.joinById(roomId);
    });

  $('#send').onclick = () => sendChat();
  $<HTMLInputElement>('#chatbox').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') sendChat();
  });
  function sendChat(): void {
    const box = $<HTMLInputElement>('#chatbox');
    const text = box.value.trim();
    if (!text) return;
    box.value = '';
    void Rest.postMessage(text);
  }

  async function challenge(toId: string, toName: string, isBot: boolean): Promise<void> {
    if (isBot) {
      enter(async () => {
        const { roomId } = await Rest.createMatch({ vsBot: true, botDifficulty: $<HTMLSelectElement>('#diff').value, holeCount: Number($<HTMLSelectElement>('#holes').value) });
        await net.joinById(roomId);
      });
      return;
    }
    try {
      await Rest.challenge(toId, toName);
      $('#status').textContent = `Challenge sent to ${toName}…`;
    } catch (e) {
      $('#status').textContent = `Could not challenge: ${(e as Error).message}`;
    }
  }

  let leaderboardAt = 0;

  async function poll(): Promise<void> {
    try {
      await Rest.heartbeat();
      $('#status').textContent = document.hidden ? 'Away' : 'Online';
      const { users } = await Rest.users();
      const myId = getUserId();
      $('#players').innerHTML = users
        .filter((u) => u.id !== myId)
        .map((u) => {
          const canChallenge = u.isBot || u.status !== 'in-game';
          return `<div class="player"><span class="p-name">${u.isBot ? '🤖 ' : statusChip(u.status)}${escapeHtml(u.name)}</span><button class="btn small chal" data-id="${u.id}" data-name="${escapeHtml(u.name)}" data-bot="${u.isBot}" ${canChallenge ? '' : 'disabled'}>Challenge</button></div>`;
        })
        .join('');
      $('#players')
        .querySelectorAll('.chal')
        .forEach((b) =>
          b.addEventListener('click', () => {
            const t = b as HTMLElement;
            void challenge(t.dataset.id!, t.dataset.name!, t.dataset.bot === 'true');
          }),
        );

      // challenges
      const ch = await Rest.getChallenges();
      $('#challenges').innerHTML = ch.incoming
        .map((c) => `<div class="challenge"><span>${escapeHtml(c.fromName)} challenges you!</span><button class="btn small accept" data-id="${c.id}">Accept</button><button class="btn small decline" data-id="${c.id}">Decline</button></div>`)
        .join('');
      $('#challenges')
        .querySelectorAll('.accept')
        .forEach((b) =>
          b.addEventListener('click', () => {
            const id = (b as HTMLElement).dataset.id!;
            enter(async () => {
              const { roomId } = await Rest.createMatch({ holeCount: Number($<HTMLSelectElement>('#holes').value), expectedPlayers: 2 });
              await net.joinById(roomId);
              await Rest.respondChallenge(id, true, roomId);
            });
          }),
        );
      $('#challenges')
        .querySelectorAll('.decline')
        .forEach((b) => b.addEventListener('click', () => void Rest.respondChallenge((b as HTMLElement).dataset.id!, false)));

      // accepted outgoing → join
      for (const o of ch.outgoing) {
        if (o.status === 'accepted' && o.roomId) {
          enter(async () => {
            await net.joinById(o.roomId!);
          });
          break;
        }
      }

      // live games (spectate)
      try {
        const { games } = await Rest.activeGames();
        $('#games-title').style.display = games.length ? '' : 'none';
        $('#games').innerHTML = games
          .map((g) => `<div class="player"><span class="p-name">🏌 ${escapeHtml(g.hostName)}'s game · ${g.playerCount} playing</span><button class="btn small watch" data-id="${g.roomId}">Watch</button></div>`)
          .join('');
        $('#games')
          .querySelectorAll('.watch')
          .forEach((b) =>
            b.addEventListener('click', () => {
              const roomId = (b as HTMLElement).dataset.id!;
              enter(async () => {
                await net.joinById(roomId, { spectate: true });
              });
            }),
          );
      } catch {
        /* non-fatal */
      }

      // leaderboard (refresh every ~30s)
      if (Date.now() - leaderboardAt > 30_000) {
        leaderboardAt = Date.now();
        try {
          const { leaderboard } = await Rest.leaderboard();
          if (leaderboard.length) {
            $('#leaderboard').innerHTML = leaderboard
              .slice(0, 5)
              .map((r, i) => `<div class="lb-row"><span>${i + 1}. ${escapeHtml(r.nickname)}</span><span class="muted">${r.gamesWon}W / ${r.gamesPlayed}P</span></div>`)
              .join('');
          }
        } catch {
          /* non-fatal */
        }
      }

      // chat
      const { messages } = await Rest.messages(chatAfter);
      if (messages.length) {
        const chat = $('#chat');
        for (const m of messages) {
          chatAfter = Math.max(chatAfter, m.ts);
          const line = el('div', m.isSystem ? 'chat-line sys' : 'chat-line');
          line.innerHTML = m.isSystem ? `<i>${escapeHtml(m.text)}</i>` : `<b>${escapeHtml(m.name)}:</b> ${escapeHtml(m.text)}`;
          chat.append(line);
        }
        chat.scrollTop = chat.scrollHeight;
      }
    } catch {
      $('#status').textContent = 'Offline — server unreachable';
    }
  }

  void poll();
  pollTimer = setInterval(poll, 4000);

  // ── Presence lifecycle ─────────────────────────────────────────
  listeners = new AbortController();
  const { signal } = listeners;

  // Tab hidden → announce "away" right away (keepalive survives throttling).
  // Tab visible → announce "online" and refresh everything immediately.
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.hidden) void Rest.heartbeat('away', true).catch(() => {});
      else void poll();
    },
    { signal },
  );

  // Actual departures (close / navigate away). pagehide is the reliable
  // signal on mobile; beforeunload covers older desktop browsers.
  window.addEventListener('pagehide', () => Rest.leaveBeacon(), { signal });
  window.addEventListener('beforeunload', () => Rest.leaveBeacon(), { signal });
  // Restored from bfcache → we told the server we left; check back in.
  window.addEventListener('pageshow', () => void poll(), { signal });
}

function cleanup(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  listeners?.abort();
  listeners = null;
}
